import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CreditCard, Printer, X } from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Button, ConfirmDialog, Input, Select } from "../../components/ui";
import { descargarReciboPdf, registrarPago, type FormaPagoCaja } from "./api";
import { mostrarMontoCLP, parsearMontoCLP } from "./montoInput";
import { refrescarTrasMovimientoDePago } from "./refrescar";

// Sólo dinero que entró: el saldo a favor lo aplica el sistema solo.
const FORMAS: FormaPagoCaja[] = ["Efectivo", "Transferencia", "Tarjeta", "Cheque"];

/** Hoy en formato YYYY-MM-DD local (para el input date y su máximo). */
function hoyIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

const schema = z.object({
  monto_pagado: z.coerce.number().int().positive("Debe ser positivo"),
  forma_pago: z.enum(["Efectivo", "Transferencia", "Tarjeta", "Cheque"]),
  fecha_pago: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Fecha inválida"),
  nro_comprobante: z.string().max(50).optional(),
  observaciones: z.string().max(255).optional(),
});

type FormValues = z.infer<typeof schema>;

/** "2026-10-15" -> "15-10-2026" para el resumen de confirmación. */
function fechaCorta(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}-${m}-${a}`;
}

interface Props {
  socioId: number;
  contratoId: number;
  totalPendiente: number;
  /** Crédito del socio que se aplicará solo antes del dinero nuevo. */
  saldoAFavor?: number;
  /** Llegó desde "Registrar pago" (Caja): trae el formulario a la vista y
   *  pone el cursor en el monto (auditoría NAV-01). */
  enfocar?: boolean;
}

export function RegistrarPagoForm({
  socioId,
  totalPendiente,
  saldoAFavor = 0,
  enfocar = false,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);
  // Una key por intento de registro: un doble envío del mismo pago (doble
  // clic, reintento de red) no se cobra dos veces. Se renueva al terminar,
  // también si falla: el backend guarda el error con esa key.
  const idempotencyKey = useRef(crypto.randomUUID());
  // El pago se confirma con un resumen antes de enviarse: es dinero.
  const [porConfirmar, setPorConfirmar] = useState<FormValues | null>(null);
  const [ultimoPago, setUltimoPago] = useState<{ id: number; monto: number } | null>(
    null,
  );
  const [descargando, setDescargando] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setFocus,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      monto_pagado: totalPendiente > 0 ? totalPendiente : 0,
      forma_pago: "Efectivo",
      fecha_pago: hoyIso(),
      nro_comprobante: "",
      observaciones: "",
    },
  });

  const esRetroactivo = watch("fecha_pago") < hoyIso();

  const seccionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!enfocar) return;
    seccionRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    setFocus("monto_pagado", { shouldSelect: true });
  }, [enfocar, setFocus]);

  const mutation = useMutation({
    mutationFn: (payload: Parameters<typeof registrarPago>[0]) =>
      registrarPago(payload, idempotencyKey.current),
    onSettled: () => {
      idempotencyKey.current = crypto.randomUUID();
    },
    onSuccess: (pago) => {
      toast.success(
        `Pago #${pago.id} registrado por ${formatCLP(pago.monto_pagado)} (${pago.detalles.length} boleta(s))`,
      );
      setPorConfirmar(null);
      setUltimoPago({ id: pago.id, monto: pago.monto_pagado });
      // Monto en 0 y no en el pendiente que había al abrir la página: tras
      // pagar, ese valor ya no es la deuda y reenviarlo cobraría dos veces.
      reset({
        monto_pagado: 0,
        forma_pago: "Efectivo",
        fecha_pago: hoyIso(),
        nro_comprobante: "",
        observaciones: "",
      });
      refrescarTrasMovimientoDePago(qc, socioId);
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo registrar el pago";
      setPorConfirmar(null);
      setServerError(msg);
      toast.error(msg);
    },
  });

  const onSubmit = (values: FormValues) => {
    setServerError(null);
    setUltimoPago(null);
    setPorConfirmar(values);
  };

  const confirmar = () => {
    if (!porConfirmar) return;
    const values = porConfirmar;
    mutation.mutate({
      socio_id: socioId,
      monto_pagado: values.monto_pagado,
      forma_pago: values.forma_pago,
      // Mediodía local: evita que un date-only se corra de día al pasar por UTC.
      fecha_pago: `${values.fecha_pago}T12:00:00`,
      nro_comprobante: values.nro_comprobante?.trim() || null,
      observaciones: values.observaciones?.trim() || null,
    });
  };

  const imprimirRecibo = async (pagoId: number) => {
    setDescargando(true);
    try {
      await descargarReciboPdf(pagoId);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "No se pudo descargar el recibo");
    } finally {
      setDescargando(false);
    }
  };

  // Cómo queda la cuenta con el monto a confirmar (el crédito a favor se
  // aplica antes, así que la deuda que cubre el dinero nuevo es la neta).
  const deudaNeta = Math.max(0, totalPendiente - saldoAFavor);
  const montoConf = porConfirmar?.monto_pagado ?? 0;
  const efecto =
    montoConf >= deudaNeta
      ? deudaNeta > 0
        ? montoConf > deudaNeta
          ? `Cubre toda la deuda y quedan ${formatCLP(montoConf - deudaNeta)} a favor del socio.`
          : "Cubre toda la deuda pendiente."
        : `El socio no tiene deuda: los ${formatCLP(montoConf)} quedan a favor.`
      : `Quedan ${formatCLP(deudaNeta - montoConf)} pendientes.`;

  return (
    <section
      ref={seccionRef}
      id="registrar-pago"
      className="scroll-mt-4 bg-white rounded-lg border border-neutral-200"
    >
      <header className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
        <CreditCard className="h-4 w-4" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-neutral-900">Registrar pago</h3>
        {totalPendiente > 0 && (
          <button
            type="button"
            onClick={() => setValue("monto_pagado", totalPendiente)}
            className="ml-auto text-xs text-brand-700 hover:text-brand-800"
          >
            Pagar todo: {formatCLP(totalPendiente)}
          </button>
        )}
      </header>
      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-3">
        {saldoAFavor > 0 && (
          <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            El socio tiene <strong>{formatCLP(saldoAFavor)}</strong> a favor: se
            aplica solo a sus boletas antes del dinero que registres acá.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Texto con formato CLP mientras se escribe ("$171.741"); el
              valor del formulario es el entero. */}
          <Controller
            control={control}
            name="monto_pagado"
            render={({ field }) => (
              <Input
                label="Monto (CLP)"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                ref={field.ref}
                name={field.name}
                value={mostrarMontoCLP(field.value)}
                onChange={(e) => field.onChange(parsearMontoCLP(e.target.value))}
                onBlur={field.onBlur}
                className="tabular-nums"
                error={errors.monto_pagado?.message}
                disabled={isSubmitting || mutation.isPending}
              />
            )}
          />

          <Select
            label="Forma de pago"
            {...register("forma_pago")}
            disabled={isSubmitting || mutation.isPending}
          >
            {FORMAS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>

          <Input
            label="Fecha del pago"
            type="date"
            max={hoyIso()}
            {...register("fecha_pago")}
            error={errors.fecha_pago?.message}
            disabled={isSubmitting || mutation.isPending}
          />

          <Input
            label="Nro. comprobante (opcional)"
            type="text"
            maxLength={50}
            {...register("nro_comprobante")}
            disabled={isSubmitting || mutation.isPending}
          />
        </div>

        <Input
          label={esRetroactivo ? "Motivo (obligatorio: fecha anterior a hoy)" : "Observaciones"}
          type="text"
          maxLength={255}
          {...register("observaciones")}
          disabled={isSubmitting || mutation.isPending}
        />

        {esRetroactivo && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Un pago con fecha de un día anterior sólo lo puede registrar quien
            cierra la caja, con motivo, y queda en la bitácora de auditoría.
          </p>
        )}

        {ultimoPago && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden />
            <span className="flex-1 min-w-0">
              Pago #{ultimoPago.id} registrado por{" "}
              <strong>{formatCLP(ultimoPago.monto)}</strong>.
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void imprimirRecibo(ultimoPago.id)}
              disabled={descargando}
              icon={Printer}
            >
              {descargando ? "Descargando…" : "Imprimir recibo"}
            </Button>
            <button
              type="button"
              onClick={() => setUltimoPago(null)}
              className="grid h-7 w-7 place-items-center rounded-md text-emerald-700 hover:bg-emerald-100"
              aria-label="Cerrar aviso"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        )}

        {serverError && (
          <p
            role="alert"
            className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
          >
            {serverError}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="submit"
            disabled={isSubmitting || mutation.isPending}
          >
            {mutation.isPending ? "Registrando…" : "Registrar pago"}
          </Button>
        </div>

        <p className="text-xs text-neutral-500">
          El pago se aplica a las boletas pendientes, de la más antigua a la
          más nueva. Lo que sobra queda como saldo a favor del socio.
        </p>
      </form>
      <ConfirmDialog
        open={porConfirmar !== null}
        tone="default"
        title="Confirmar pago"
        confirmLabel={`Registrar ${formatCLP(montoConf)}`}
        loading={mutation.isPending}
        onCancel={() => setPorConfirmar(null)}
        onConfirm={confirmar}
        description={
          porConfirmar && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt>Monto</dt>
              <dd className="font-semibold text-neutral-900 tabular-nums">
                {formatCLP(porConfirmar.monto_pagado)}
              </dd>
              <dt>Forma</dt>
              <dd>{porConfirmar.forma_pago}</dd>
              <dt>Fecha</dt>
              <dd>{fechaCorta(porConfirmar.fecha_pago)}</dd>
              <dd className="col-span-2 mt-2">{efecto}</dd>
            </dl>
          )
        }
      />
    </section>
  );
}
